import React, { useState } from 'react';
import PropTypes from 'prop-types';

const Pagination = ({
  currentCoursePage,
  totalCourses,
  onPageChange,
  pageSize,
  pageRangeDisplayed,
  marginPagesDisplayed
}) => {
  const [inputValue, setInputValue] = useState('');
  const totalPages = Math.ceil(totalCourses / pageSize);

  const generatePageNumbers = () => {
    const pageNumbers = [];
    const leftSide = Math.max(currentCoursePage - Math.floor(pageRangeDisplayed / 2), 4);
    const rightSide = Math.min(currentCoursePage + Math.floor(pageRangeDisplayed / 2), totalPages - 3);

    // Always show the first 3 pages
    for (let i = 1; i <= Math.min(3, totalPages); i++) {
      pageNumbers.push(i);
    }

    // Add ellipsis if needed
    if (leftSide > 4) {
      pageNumbers.push('...');
    }

    // Show the middle pages
    for (let i = leftSide; i <= rightSide; i++) {
      pageNumbers.push(i);
    }

    // Add ellipsis if needed
    if (rightSide < totalPages - 3) {
      pageNumbers.push('...');
    }

    // Always show the last 3 pages
    for (let i = Math.max(totalPages - 2, 4); i <= totalPages; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const pageNumbers = generatePageNumbers();

  const handleInputChange = (event) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleJumpToPage = () => {
    const pageNumber = parseInt(inputValue, 10);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setInputValue('');
    }
  };

  return (
    <nav className='pagination-wrap'>
      <div className="pagination-jump">
        <span className="total-pages pe-2"><strong>Page {currentCoursePage} of {totalPages}</strong></span>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Go to page"
          className="pagination-input"
        />
        <button onClick={handleJumpToPage} className="pagination-go-button">
          Go
        </button>
      </div>
      <ul className="pagination">
        <li className={`page-item ${currentCoursePage === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(1)}
            disabled={currentCoursePage === 1}
          >
            {'«'}
          </button>
        </li>
        <li className={`page-item ${currentCoursePage === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(currentCoursePage - 1)}
            disabled={currentCoursePage === 1}
          >
            {'‹'}
          </button>
        </li>
        {pageNumbers.map((number, index) => (
          <li
            key={index}
            className={`page-item ${number === currentCoursePage ? 'active' : ''} ${number === '...' ? 'disabled' : ''}`}
          >
            {number === '...' ? (
              <button className="page-link">...</button>
            ) : (
              <button className="page-link" onClick={() => onPageChange(number)}>
                {number}
              </button>
            )}
          </li>
        ))}
        <li className={`page-item ${currentCoursePage === totalPages ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(currentCoursePage + 1)}
            disabled={currentCoursePage === totalPages}
          >
            {'›'}
          </button>
        </li>
        <li className={`page-item ${currentCoursePage === totalPages ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(totalPages)}
            disabled={currentCoursePage === totalPages}
          >
            {'»'}
          </button>
        </li>
      </ul>
    </nav>
  );
};

Pagination.propTypes = {
  currentCoursePage: PropTypes.number.isRequired,
  totalCourses: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageRangeDisplayed: PropTypes.number,
  marginPagesDisplayed: PropTypes.number,
};

Pagination.defaultProps = {
  pageRangeDisplayed: 3,
  marginPagesDisplayed: 2,
};

export default Pagination;
